import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import http from '../../http-common';
import { useTranslation } from '../../hooks/useTranslation';
import { initializeCurrency } from '../../utils/currencyUtils';

const BudgetList = () => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    client_id: ''
  });
  const [clients, setClients] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendDialog, setSendDialog] = useState({ open: false, budgetId: null });
  const [sendRecipients, setSendRecipients] = useState([{ id: Date.now(), email: '' }]);
  const [emailMessage, setEmailMessage] = useState('');
  const [storeEmails, setStoreEmails] = useState(true);
  const [updateStatusToSent, setUpdateStatusToSent] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Initialize currency settings first
    initializeCurrency().then(() => {
      fetchBudgets();
      fetchClients();
      fetchStatistics();
    });
  }, [page, rowsPerPage, filters]);

  const fetchBudgets = async () => {
    setLoading(true);
    try {
      // Filter out empty values to avoid sending empty strings to API
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== '' && value !== null && value !== undefined)
      );
      
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...cleanFilters
      };
      
      console.log('Fetching budgets with params:', params);
      const response = await http.get('/test/budgets', { params });
      console.log('Budgets response:', response.data);
      
      const budgetsData = response.data?.data?.data || [];
      const totalData = response.data?.data?.total || 0;
      
      console.log('Budgets data:', budgetsData);
      console.log('Total:', totalData);
      
      setBudgets(budgetsData);
      setTotal(totalData);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setBudgets([]);
      setTotal(0);
      showSnackbar(t('budgets.errorLoadingBudgets') + ': ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await http.get('/clients');
      setClients(response.data?.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await http.get('/test/budgets/statistics/overview');
      setStatistics(response.data?.data || {});
    } catch (error) {
      console.error('Error loading statistics:', error);
      setStatistics({});
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setFilters(prev => ({ ...prev, search: event.target.value }));
    setPage(0);
  };

  const handleStatusFilter = (event) => {
    setFilters(prev => ({ ...prev, status: event.target.value }));
    setPage(0);
  };

  const handleClientFilter = (event) => {
    setFilters(prev => ({ ...prev, client_id: event.target.value }));
    setPage(0);
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await http.delete(`/budgets/${deleteDialog.id}`);
      showSnackbar(t('budgets.budgetDeletedSuccess'));
      fetchBudgets();
    } catch (error) {
      showSnackbar(t('budgets.errorDeletingBudget'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleExport = async (id, type) => {
    try {
      const response = await http.get(`/test/budgets/${id}/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_${id}.${type === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSnackbar(t('budgets.exportedSuccess', { type: type.toUpperCase() }));
    } catch (error) {
      console.error('Export error:', error);
      showSnackbar(t('budgets.errorExporting', { type: type.toUpperCase() }) + ': ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const openSendDialog = (budget) => {
    const recipients = budget.recipients || [];
    if (recipients.length > 0) {
      setSendRecipients(recipients.map((recipient) => ({ id: recipient.id, email: recipient.email })));
    } else {
      setSendRecipients([{ id: Date.now(), email: '' }]);
    }
    setEmailMessage('');
    setStoreEmails(true);
    setSendDialog({ open: true, budgetId: budget.id });
  };

  const addSendRecipient = () => {
    setSendRecipients(prev => [...prev, { id: Date.now(), email: '' }]);
  };

  const updateSendRecipient = (recipientId, value) => {
    setSendRecipients(prev => prev.map(recipient => recipient.id === recipientId ? { ...recipient, email: value } : recipient));
  };

  const removeSendRecipient = (recipientId) => {
    setSendRecipients(prev => {
      if (prev.length === 1) return prev;
      return prev.filter(recipient => recipient.id !== recipientId);
    });
  };

  const confirmSend = async () => {
    try {
      const emails = sendRecipients
        .map(recipient => recipient.email?.trim())
        .filter(email => email);

      if (emails.length === 0) {
        showSnackbar(t('budgets.pleaseProvideEmail'), 'error');
        return;
      }

      await http.post(`/budgets/${sendDialog.budgetId}/send-email`, {
        emails,
        message: emailMessage,
        store_emails: storeEmails,
        update_status_to_sent: updateStatusToSent,
      });

      showSnackbar(t('budgets.budgetSentSuccess'));
      setSendDialog({ open: false, budgetId: null });
      fetchBudgets();
    } catch (error) {
      showSnackbar(t('budgets.errorSendingBudget') + ': ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      sent: 'primary',
      approved: 'success',
      rejected: 'error'
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (value) => {
    const { formatCurrencySync } = require('../../utils/currencyUtils');
    return formatCurrencySync(value);
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
          {t ('budgets.title')}
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                  {t ('budgets.totalbudgets')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {statistics.total_budgets || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                  {t ('budgets.draft')}
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {statistics.draft_budgets || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                  {t ('budgets.approved')}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {statistics.approved_budgets || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                  {t ('budgets.totalValue')}
                  </Typography>
                  <Typography variant="h4" color="secondary">
                    {formatCurrency(statistics.total_value || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/budgets/new')}
            >
              {t ('budgets.newBudget')}
            </Button>
            
            <TextField
              placeholder={t ('budgets.srBudget')}
              value={filters.search}
              onChange={handleSearch}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t ('common.status')}</InputLabel>
              <Select
                value={filters.status}
                onChange={handleStatusFilter}
                label="Status"
              >
                <MenuItem value="">{t ('budgets.all')}</MenuItem>
                <MenuItem value="draft">{t ('budgets.draft')}</MenuItem>
                <MenuItem value="sent">{t ('budgets.sent')}</MenuItem>
                <MenuItem value="approved">{t ('budgets.approved')}</MenuItem>
                <MenuItem value="rejected">{t ('budgets.rejected')}</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t ('clients.client')}</InputLabel>
              <Select
                value={filters.client_id}
                onChange={handleClientFilter}
                label="Client"
              >
                <MenuItem value="">{t ('clients.allclients')}</MenuItem>
                {clients.map(client => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t ('common.name')}</TableCell>
                  <TableCell>{t ('clients.client')}</TableCell>
                  <TableCell>{t ('common.status')}</TableCell>
                  <TableCell>{t ('common.total')}</TableCell>
                  <TableCell>{t ('common.date')}</TableCell>
                  <TableCell align="center">{t ('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !budgets || budgets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                    {t ('budgets.msgnobudgetfound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  budgets.map((budget) => (
                    <TableRow key={budget.id} hover>
                      <TableCell>{budget.name}</TableCell>
                      <TableCell>{budget.client?.name || 'No client'}</TableCell>
                      <TableCell>
                        <Chip
                          label={t('budgets.' + (budget.status || '').toLowerCase())}
                          color={getStatusColor(budget.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(budget.total)}</TableCell>
                      <TableCell>{new Date(budget.created_at).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={t ('budgets.view')}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/budgets/${budget.id}`)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t ('budgets.edit')}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/budgets/${budget.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t ('budgets.send')}>
                            <IconButton
                              size="small"
                              onClick={() => openSendDialog(budget)}
                            >
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t ('budgets.exportPdf')}>
                            <IconButton
                              size="small"
                              onClick={() => handleExport(budget.id, 'pdf')}
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t ('budgets.exportExcel')}>
                            <IconButton
                              size="small"
                              onClick={() => handleExport(budget.id, 'excel')}
                            >
                              <ExcelIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t ('common.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(budget.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 25]}
            labelRowsPerPage={t('pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => 
              t('pagination.displayedRows', { from, to, count })
            }
          />
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle> {t ('budgets.deletebudget')}</DialogTitle>
        <DialogContent>
          <Typography>{t ('budgets.msgdeletebudget')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
          {t ('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
          {t ('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={sendDialog.open}
        onClose={() => setSendDialog({ open: false, budgetId: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('budgets.sendBudgetViaEmail')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('budgets.sendDialogDescription')}
          </Typography>
          {sendRecipients.map((recipient, index) => (
            <Box key={recipient.id} display="flex" gap={1} alignItems="center" mt={1}>
              <TextField
                fullWidth
                label={t('budgets.emailNumber', { n: index + 1 })}
                value={recipient.email}
                onChange={(e) => updateSendRecipient(recipient.id, e.target.value)}
                placeholder={t('budgets.emailPlaceholder')}
              />
              <Button
                color="error"
                variant="outlined"
                onClick={() => removeSendRecipient(recipient.id)}
                disabled={sendRecipients.length === 1}
              >
                {t('budgets.remove')}
              </Button>
            </Box>
          ))}
          <Box mt={1}>
            <Button variant="text" onClick={addSendRecipient}>
              {t('budgets.addRecipient')}
            </Button>
          </Box>
          <TextField
            fullWidth
            label={t('budgets.messageOptional')}
            helperText={t('budgets.emailTemplateHint')}
            multiline
            rows={3}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Box mt={2}>
            <label>
              <input
                type="checkbox"
                checked={storeEmails}
                onChange={(e) => setStoreEmails(e.target.checked)}
              />
              {' '}{t('budgets.storeNewEmails')}
            </label>
          </Box>
          <Box mt={1}>
            <label>
              <input
                type="checkbox"
                checked={updateStatusToSent}
                onChange={(e) => setUpdateStatusToSent(e.target.checked)}
              />
              {' '}{t('budgets.updateStatusToSent')}
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog({ open: false, budgetId: null })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={confirmSend}>{t('budgets.send')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetList;